import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { gql, useQuery } from "@apollo/client";
import { Card, Fade, Grid, Icon, InputLabel, Modal, Tooltip } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { getColor } from "utils/colors";
import UNIDADES from "./unidades";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};

const GET_ULTIMA_DECLARACION = gql`
  query getDeclaracionesJuradas($idEmpresa: ID!) {
    ultimaDeclaracion(idEmpresa: $idEmpresa) {
      id
      equipamientos {
        __typename @skip(if: true)
        tipo
        potencia
        descripcion
        cantidad
      }
      materiasPrimas {
        __typename @skip(if: true)
        compuestoQuimico
        unidad
        estado
        cantidadAnual
      }
      vehiculos {
        __typename @skip(if: true)
        tipo
        marcaModelo
        dominio
        tallerPropio
      }
    }
  }
`;

const ModalAnteriores = ({ open, handleClose, idEmpresa, setListado, tipo }) => {
  const shouldFetch = Boolean(open && idEmpresa);
  const { data, loading, error } = useQuery(GET_ULTIMA_DECLARACION, {
    variables: {
      idEmpresa: idEmpresa,
    },
    fetchPolicy: "no-cache",
    skip: !shouldFetch,
  });

  const [hasRecords, setHasRecords] = useState([]);
  const loadableRecords =
    tipo === "materia prima"
      ? (hasRecords || []).filter((record) => UNIDADES.includes(record.unidad))
      : hasRecords || [];
  useEffect(() => {
    if (data && data.ultimaDeclaracion) {
      if (tipo === "equipamiento") {
        setHasRecords(data.ultimaDeclaracion?.equipamientos);
      }
      if (tipo === "materia prima") {
        setHasRecords(data.ultimaDeclaracion?.materiasPrimas);
      }
      if (tipo === "vehiculos") {
        setHasRecords(data.ultimaDeclaracion?.vehiculos);
      }
    }
  }, [data, tipo]);
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6">Registros Anteriores</SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                {loading && (
                  <Grid item xs={12}>
                    <SoftTypography variant="body2">Cargando...</SoftTypography>
                  </Grid>
                )}
                {error && (
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                  >
                    <Icon fontSize="large" color="error" sx={{ mb: 1 }}>
                      error
                    </Icon>
                    <SoftTypography variant="body2" color="error">
                      Error al cargar los registros.
                    </SoftTypography>
                  </Grid>
                )}
                {!loading && !error && (
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                  >
                    <Icon fontSize="large" color="info" sx={{ mb: 1 }}>
                      info
                    </Icon>
                    <SoftTypography variant="body2">
                      {loadableRecords.length > 0
                        ? `
                        Se encontraron ${loadableRecords.length} registro/s anterior/es de ${tipo}. Presione cargar para utilizarlos en la declaración actual.
                      `
                        : "No existen registros anteriores"}
                    </SoftTypography>
                  </Grid>
                )}
                {tipo === "materia prima" && loadableRecords.length < (hasRecords?.length || 0) && (
                  <Grid item xs={12}>
                    <SoftTypography variant="body2" color="error">
                      Las materias primas con unidades anteriores no se cargarán. Ingresalas de
                      nuevo con una unidad vigente.
                    </SoftTypography>
                  </Grid>
                )}
              </Grid>
            </SoftBox>

            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftButton
                variant="gradient"
                onClick={() => {
                  handleClose();
                  setListado(loadableRecords);
                }}
                disabled={loadableRecords.length === 0}
                sx={{
                  mr: 1,
                  background: getColor("gobSecondary"),
                  color: "#fff",
                }}
              >
                <Icon sx={{ fontWeight: "light" }}>cached</Icon>
                &nbsp;Cargar
              </SoftButton>
              <SoftButton color="primary" onClick={handleClose}>
                <Icon sx={{ fontWeight: "light" }}>close</Icon>
                &nbsp;Cerrar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
};

ModalAnteriores.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  idEmpresa: PropTypes.string,
  setListado: PropTypes.func,
  tipo: PropTypes.string,
};

export default ModalAnteriores;
